import { Pill } from 'lucide-react';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Checkbox } from '~/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';

interface OtcMedicationFormProps {
  permission: boolean | undefined;
  medications: {
    acetaminophen?: boolean;
    ibuprofen?: boolean;
    coughDrops?: boolean;
    other?: string | null;
  };
  onPermissionChange: (hasPermission: boolean) => void;
  onMedicationToggle: (medication: string, checked: boolean) => void;
  onOtherMedicationChange: (value: string) => void;
  studentId: string;
}

export function OtcMedicationForm({
  permission,
  medications,
  onPermissionChange,
  onMedicationToggle,
  onOtherMedicationChange,
  studentId,
}: OtcMedicationFormProps) {
  const permissionValue = permission === true ? 'yes'
    : permission === false ? 'no'
    : undefined;

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Pill className="h-4 w-4 text-muted-foreground" />
          <Label className="text-base font-medium">Over-the-Counter Medication Permission</Label>
        </div>
        <p className="text-sm text-muted-foreground">
          I give permission for the adult staff or teachers of Immanuel Classical to give my child
          the following in case of illness:
        </p>
      </div>

      <RadioGroup
        value={permissionValue}
        onValueChange={(val) => onPermissionChange(val === 'yes')}
        className="flex gap-6"
      >
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="yes" id={`otc-yes-${studentId}`} />
          <Label htmlFor={`otc-yes-${studentId}`} className="font-normal cursor-pointer">
            Yes, I give permission
          </Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="no" id={`otc-no-${studentId}`} />
          <Label htmlFor={`otc-no-${studentId}`} className="font-normal cursor-pointer">
            No, do not administer medications
          </Label>
        </div>
      </RadioGroup>

      {permission === true && (
        <div className="ml-2 space-y-3 p-4 border rounded-lg bg-slate-50/50">
          <p className="text-sm font-medium text-muted-foreground">
            Select which medications may be administered:
          </p>

          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <Checkbox
                id={`med-acetaminophen-${studentId}`}
                checked={medications.acetaminophen || false}
                onCheckedChange={(checked) => onMedicationToggle('acetaminophen', checked as boolean)}
              />
              <Label htmlFor={`med-acetaminophen-${studentId}`} className="font-normal cursor-pointer">
                Acetaminophen (Tylenol) or generic equivalent
              </Label>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id={`med-ibuprofen-${studentId}`}
                checked={medications.ibuprofen || false}
                onCheckedChange={(checked) => onMedicationToggle('ibuprofen', checked as boolean)}
              />
              <Label htmlFor={`med-ibuprofen-${studentId}`} className="font-normal cursor-pointer">
                Ibuprofen (Advil) or generic equivalent
              </Label>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id={`med-cough-drops-${studentId}`}
                checked={medications.coughDrops || false}
                onCheckedChange={(checked) => onMedicationToggle('coughDrops', checked as boolean)}
              />
              <Label htmlFor={`med-cough-drops-${studentId}`} className="font-normal cursor-pointer">
                Cough Drops or Throat Lozenges
              </Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`med-other-${studentId}`} className="font-normal">
                Other (please specify):
              </Label>
              <Input
                id={`med-other-${studentId}`}
                value={medications.other || ''}
                onChange={(e) => onOtherMedicationChange(e.target.value)}
                placeholder="Any additional medications..."
                className="max-w-md"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
